<template>
  <HrPageLayout
    title="User Management"
    subtitle="System accounts, roles by category, and employee linkage"
    icon="👥"
    :breadcrumb-items="[
      { label: 'System Administration' },
      { label: 'User Management' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" @click="clearFilters" :disabled="loading">
        <i class="bx bx-filter-alt"></i> Clear filters
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="loadUsers" :disabled="loading">
        <i class="bx bx-refresh" :class="{ spin: loading }"></i>
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </template>

    <div class="user-mgmt-shell">
      <!-- Summary strip -->
      <div class="user-mgmt-metrics hr-mb-4">
        <div class="user-mgmt-metric">
          <span class="user-mgmt-metric-label">Total (filtered)</span>
          <strong class="user-mgmt-metric-value">{{ total }}</strong>
        </div>
        <div class="user-mgmt-metric">
          <span class="user-mgmt-metric-label">Page</span>
          <strong class="user-mgmt-metric-value">{{ page }} / {{ totalPages }}</strong>
        </div>
        <div class="user-mgmt-metric user-mgmt-metric-hint">
          <i class="bx bx-info-circle"></i>
          <span>Use filters to narrow by <strong>category</strong> and status.</span>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card user-mgmt-filters-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-search-alt"></i> Find users</h5>
        </div>
        <div class="hr-chart-body">
          <div class="user-mgmt-filter-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Search</label>
              <div class="hr-search-wrap">
                <input
                  v-model="filters.search"
                  type="text"
                  class="hr-input"
                  placeholder="Name, username, payroll no…"
                  @keyup.enter="applyFilters"
                  @input="onSearchInput"
                >
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Category</label>
              <select v-model="filters.categoryId" class="hr-select" @change="applyFilters">
                <option value="">All categories</option>
                <option v-for="c in categories" :key="c.id" :value="String(c.id)">
                  {{ c.categoryName }}{{ c.currentUsers != null ? ' (' + c.currentUsers + ')' : '' }}
                </option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" class="hr-select" @change="applyFilters">
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="pending">Pending</option>
                <option value="Blocked">Blocked</option>
              </select>
            </div>
            <div class="hr-filter-group user-mgmt-filter-actions">
              <label class="hr-form-label">&nbsp;</label>
              <div class="hr-flex-gap">
                <button type="button" class="hr-btn hr-btn-primary" @click="applyFilters">
                  <i class="bx bx-check"></i> Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card user-mgmt-table-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-group"></i> Accounts</h5>
        </div>
        <div class="hr-chart-body hr-p-0">
          <div v-if="loading" class="user-mgmt-loading">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted hr-mt-2 hr-mb-0">Loading users…</p>
          </div>
          <div v-else-if="users.length === 0" class="user-mgmt-empty">
            <i class="bx bx-user-x"></i>
            <p class="hr-mb-0">No users match your filters.</p>
          </div>
          <div v-else class="hr-table-wrapper user-mgmt-table-wrap">
            <table class="hr-table hr-table-bordered user-mgmt-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Username</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Employee</th>
                  <th>Created</th>
                  <th class="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in users" :key="u.userID">
                  <td>
                    <div class="user-mgmt-name">{{ u.names || '—' }}</div>
                  </td>
                  <td><code class="user-mgmt-code">{{ u.username }}</code></td>
                  <td>
                    <span class="hr-badge hr-badge-info">{{ u.category || '—' }}</span>
                  </td>
                  <td>
                    <span class="hr-badge" :class="statusBadgeClass(u.status)">{{ formatStatus(u.status) }}</span>
                  </td>
                  <td>
                    <template v-if="u.employeeID">
                      <span class="user-mgmt-emp">{{ employeeDisplay(u) }}</span>
                    </template>
                    <span v-else class="hr-text-muted">—</span>
                  </td>
                  <td class="user-mgmt-date">{{ formatDate(u.createdAt) }}</td>
                  <td class="text-center">
                    <div class="user-mgmt-actions">
                      <button
                        v-if="isBlocked(u.status)"
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-success"
                        :disabled="unblockingId === u.userID"
                        @click="unblockUser(u)"
                      >
                        <span v-if="unblockingId === u.userID" class="hr-spinner small"></span>
                        <i v-else class="bx bx-lock-open"></i>
                        Unblock
                      </button>
                      <button
                        v-if="u.employeeID"
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-outline-primary"
                        @click="openUserModal(u.employeeID)"
                      >
                        Manage
                      </button>
                      <span v-else-if="!isBlocked(u.status)" class="hr-text-muted hr-small">—</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="total > limit && !loading" class="user-mgmt-pagination">
            <div class="user-mgmt-page-info">
              Showing {{ (page - 1) * limit + 1 }}–{{ Math.min(page * limit, total) }} of {{ total }}
            </div>
            <nav class="hr-pagination-nav">
              <button
                type="button"
                class="hr-page-btn"
                :disabled="page <= 1"
                @click="goPage(page - 1)"
              >
                <i class="bx bx-chevron-left"></i>
              </button>
              <button
                type="button"
                class="hr-page-btn"
                :disabled="page >= totalPages"
                @click="goPage(page + 1)"
              >
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <UserManagementModal
      v-if="selectedEmployeeId"
      :employee="selectedEmployee"
      :reveal-sensitive="true"
      @close="onModalClose"
      @user-updated="loadUsers"
    />
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import UserManagementModal from "@/components/employeeRegister/modals/UserManagementModal.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "UserManagement",
  components: { HrPageLayout, UserManagementModal },
  data() {
    return {
      loading: false,
      users: [],
      categories: [],
      total: 0,
      page: 1,
      limit: 50,
      filters: {
        search: "",
        status: "",
        categoryId: ""
      },
      searchDebounce: null,
      selectedEmployeeId: null,
      selectedEmployee: null,
      unblockingId: null
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    }
  },
  mounted() {
    this.loadCategories();
    this.loadUsers();
  },
  beforeUnmount() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  methods: {
    loadCategories() {
      const data = new FormData();
      data.append("function", "getCategories");
      execute(data, authUrl)
        .then((res) => {
          if (res.data && res.data.success && res.data.data) {
            this.categories = res.data.data.categories || [];
          }
        })
        .catch(() => {
          this.categories = [];
        });
    },
    loadUsers() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getUsersList");
      data.append("page", String(this.page));
      data.append("limit", String(this.limit));
      data.append("search", this.filters.search || "");
      data.append("status", this.filters.status || "");
      if (this.filters.categoryId) {
        data.append("categoryId", String(this.filters.categoryId));
      }

      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data && res.data.success) {
            this.users = res.data.data.users || [];
            this.total = res.data.data.total ?? 0;
          } else {
            this.users = [];
            this.total = 0;
          }
        })
        .catch(() => {
          this.loading = false;
          this.users = [];
          this.total = 0;
        });
    },
    applyFilters() {
      this.page = 1;
      this.loadUsers();
    },
    clearFilters() {
      this.filters = { search: "", status: "", categoryId: "" };
      this.page = 1;
      this.loadUsers();
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.applyFilters();
      }, 450);
    },
    goPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.loadUsers();
    },
    onModalClose() {
      this.selectedEmployeeId = null;
      this.selectedEmployee = null;
      this.loadUsers();
    },
    openUserModal(employeeId) {
      const u = this.users.find((x) => x.employeeID == employeeId);
      this.selectedEmployee = u
        ? {
            id: u.employeeID,
            firstName: u.firstName,
            lastName: u.lastName,
            empNo: u.empNo,
            email: u.employeeEmail,
            nationalId: u.username
          }
        : { id: employeeId };
      this.selectedEmployeeId = employeeId;
    },
    isBlocked(status) {
      return String(status || "").trim().toLowerCase() === "blocked";
    },
    async unblockUser(u) {
      if (!u?.userID) return;
      if (!confirm(`Unblock ${u.names || u.username}? They will be able to sign in again.`)) {
        return;
      }
      this.unblockingId = u.userID;
      try {
        const data = new FormData();
        data.append("function", "unblockUserAccount");
        data.append("userId", u.userID);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success?.(res.data.message || "User unblocked") ||
            alert(res.data.message || "User unblocked");
          this.loadUsers();
        } else {
          throw new Error(res?.data?.message || "Failed to unblock user");
        }
      } catch (error) {
        this.$toast?.error?.(error.message || "Failed to unblock user") ||
          alert(error.message || "Failed to unblock user");
      } finally {
        this.unblockingId = null;
      }
    },
    employeeDisplay(u) {
      const name = [u.firstName, u.lastName].filter(Boolean).join(" ");
      if (name) return name;
      if (u.empNo) return u.empNo;
      return "#" + u.employeeID;
    },
    statusBadgeClass(s) {
      if (!s) return "hr-badge-muted";
      const v = String(s).toLowerCase();
      if (v === "active") return "hr-badge-success";
      if (v === "inactive") return "hr-badge-muted";
      if (v === "suspended") return "hr-badge-danger";
      if (v === "pending") return "hr-badge-warning";
      if (v === "blocked") return "hr-badge-danger";
      return "hr-badge-muted";
    },
    formatStatus(s) {
      if (!s) return "—";
      return String(s).charAt(0).toUpperCase() + String(s).slice(1);
    },
    formatDate(d) {
      if (!d) return "—";
      return new Date(d).toLocaleDateString("en-GB", { dateStyle: "medium" });
    }
  }
};
</script>

<style scoped>
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.user-mgmt-shell {
  max-width: 1320px;
  margin: 0 auto;
  padding: 8px 12px 32px;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .user-mgmt-shell {
    padding: 12px 24px 40px;
  }
}

.user-mgmt-metrics {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.user-mgmt-metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
}

.user-mgmt-metric-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.user-mgmt-metric-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.user-mgmt-metric-hint {
  flex: 1;
  min-width: 200px;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.user-mgmt-metric-hint i {
  font-size: 20px;
  color: #94a3b8;
}

.user-mgmt-filters-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
}

.user-mgmt-filter-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .user-mgmt-filter-grid {
    grid-template-columns: 1.4fr 1fr 1fr auto;
    align-items: end;
  }
}

.user-mgmt-filter-actions .hr-flex-gap {
  display: flex;
  gap: 8px;
}

.hr-search-wrap {
  position: relative;
}

.hr-search-wrap .search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.hr-search-wrap .hr-input {
  padding-right: 36px;
}

.user-mgmt-table-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
}

.user-mgmt-table-wrap {
  margin: 0;
}

.user-mgmt-table {
  font-size: 14px;
}

.user-mgmt-table thead th {
  background: #f8fafc;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  padding: 14px 16px;
}

.user-mgmt-table tbody td {
  padding: 14px 16px;
  vertical-align: middle;
  border-color: #f1f5f9;
}

.user-mgmt-name {
  font-weight: 600;
  color: #0f172a;
}

.user-mgmt-code {
  font-size: 13px;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  color: #334155;
}

.user-mgmt-emp {
  font-size: 13px;
  color: #334155;
}

.user-mgmt-date {
  color: #64748b;
  font-size: 13px;
  white-space: nowrap;
}

.user-mgmt-loading,
.user-mgmt-empty {
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
}

.user-mgmt-empty i {
  font-size: 48px;
  opacity: 0.35;
  display: block;
  margin-bottom: 12px;
}

.user-mgmt-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.user-mgmt-page-info {
  font-size: 13px;
  color: #64748b;
}

.hr-pagination-nav {
  display: flex;
  gap: 8px;
}

.hr-page-btn {
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
}

.hr-page-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.hr-page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Badge variants used with hr-badge base from shared CSS */
.hr-badge-success { background: #dcfce7; color: #166534; }
.hr-badge-warning { background: #fef3c7; color: #92400e; }
.hr-badge-danger { background: #fee2e2; color: #991b1b; }
.hr-badge-muted { background: #f1f5f9; color: #64748b; }
.hr-badge-info { background: #e0f2fe; color: #0369a1; }

.user-mgmt-actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.hr-btn-success {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}

.hr-btn-success:hover:not(:disabled) {
  background: #15803d;
  border-color: #15803d;
  color: #fff;
}

.hr-spinner.small {
  width: 14px;
  height: 14px;
  border-width: 2px;
  display: inline-block;
}
</style>
